import { useQuery } from "@tanstack/react-query";
import { contractLocale, type Currency, type Language, type MoneyLocale } from "@talentkontor/domain";
import { api } from "../../../data";
import { today as todayIn } from "../../../lib/format";
import type { StaffWorkspace } from "../../../app/session";
import { loadOrgDaten, loadVertrag, type VertragDetail, type OrgDaten } from "./daten";
import { contractContext, quellen } from "./kontext";

/** `enabled`: die Vorschau (K-511) lädt erst, wenn sie offen ist; gleicher Schlüssel wie Editor und Detail (geteilter Zwischenspeicher). */
export function useVertragDaten(ws: StaffWorkspace, id: string, enabled = true) {
  const orgId = ws.org.org_id;
  const detail = useQuery({ queryKey: ["contracts", orgId, id], queryFn: () => loadVertrag(api, orgId, id), enabled: enabled && !!id });
  const org = useQuery({ queryKey: ["org_daten", orgId], queryFn: () => loadOrgDaten(api, orgId), enabled });
  return { detail, org };
}

import { NUMBER_PREVIEW } from "./modell";
export { NUMBER_PREVIEW };

export type KontextOverrides = {
  talent?: VertragDetail["talent"]; brand?: VertragDetail["brand"]; deal?: VertragDetail["deal"]; contacts?: VertragDetail["contacts"];
  terms?: VertragDetail["terms"]; items?: VertragDetail["items"]; periods?: VertragDetail["periods"]; representedBy?: string | null; title?: string;
  /** Beiträge und Plattform-Katalog einer im Editor neu gewählten Kooperation (0068) */
  beitraege?: VertragDetail["beitraege"]; plattformen?: VertragDetail["plattformen"];
  number?: string | null; date?: string; startsOn?: string | null; endsOn?: string | null;
  /** Laufzeit und Vertretung aus dem Editor (noch nicht gespeichert) */
  term?: { term_months: number | null; auto_renew: boolean; renewal_months: number | null; notice_days: number | null;
    exclusive_management: boolean; exclusive_scope: string | null; tail_months: number | null };
  agencySignatures?: number;
  guardians?: readonly string[];
  /** 0071: im Editor gewählte Vertretung der Agentur (null: die ersten der Firmenangaben) */
  agencyRepresentatives?: { name: string; position?: string | null }[] | null;
};

/** Werte der Systemplatzhalter für diesen Vertrag (aktuelle Stammdaten, Firmenangaben in der Vertragssprache). */
export function kontextFuer(ws: StaffWorkspace, d: VertragDetail, org: OrgDaten, overrides: KontextOverrides = {}) {
  const today = todayIn(ws.org.timezone);
  const c = d.contract;
  const lang = c.language;
  const label = overrides.number !== undefined ? overrides.number : c.contract_number_label ?? (c.contract_number != null ? null : NUMBER_PREVIEW[lang]);
  const term = overrides.term ?? {
    term_months: c.term_months, auto_renew: c.auto_renew, renewal_months: c.renewal_months, notice_days: c.notice_days,
    exclusive_management: c.exclusive_management, exclusive_scope: c.exclusive_scope, tail_months: c.tail_months,
  };
  return contractContext(quellen({
    orgName: ws.org.name, org,
    talent: overrides.talent !== undefined ? overrides.talent : d.talent,
    brand: overrides.brand !== undefined ? overrides.brand : d.brand,
    representedBy: overrides.representedBy !== undefined ? overrides.representedBy : d.representedBy,
    contacts: overrides.contacts ?? d.contacts,
    deal: overrides.deal !== undefined ? overrides.deal : d.deal,
    items: overrides.items ?? d.items,
    periods: overrides.periods ?? d.periods,
    beitraege: overrides.beitraege ?? d.beitraege ?? [],
    plattformen: overrides.plattformen ?? d.plattformen ?? [],
    terms: overrides.terms !== undefined ? overrides.terms : d.terms,
    today,
    contract: {
      number: c.contract_number, label, title: overrides.title ?? c.title,
      date: overrides.date ?? (c.sent_at ?? today).slice(0, 10), kind: d.kind ? (lang === "en" ? d.kind.name_en : d.kind.name_de) : null, language: lang,
      starts_on: overrides.startsOn !== undefined ? overrides.startsOn : c.starts_on, ends_on: overrides.endsOn !== undefined ? overrides.endsOn : c.ends_on,
      ...term, agency_signatures: overrides.agencySignatures ?? c.agency_signatures,
      agency_representatives: overrides.agencyRepresentatives !== undefined ? overrides.agencyRepresentatives : c.agency_representatives ?? null,
    },
    guardians: overrides.guardians ?? d.signers.filter((s) => s.role === "talent" && s.capacity === "legal_guardian").map((s) => s.name),
    original: d.original ? { number: d.original.contract_number_label, title: d.original.title, date: d.original.concluded_on ?? null } : null,
  }));
}

/** Formate des Vertrags: aus seiner Sprache (englisch immer en-GB), Währung aus der Kooperation (Befund 8). */
export function geldFormat(org: OrgDaten, d: Pick<VertragDetail, "deal"> & { contract: { language: Language } }): { locale: MoneyLocale; currency: Currency } {
  return { locale: contractLocale(d.contract.language, org.settings.locale), currency: ((d.deal?.currency ?? org.settings.currency) as Currency) ?? "EUR" };
}
